// Screen 1 — Order-detail Path-A return entry (screens-buyer-app §1, canvas OrdB_AppOrder).
//
// Thinking-in-React: a presentational, props-only section. It renders, per
// order line, a "Буцаах" button, a "N буцаагдсан (RET-...)" sub-line, the faded
// "Бүрэн буцаагдсан" state, and a "Бүх захиалгыг буцаах" bar. It owns NO server
// state; the order + window come from the caller (the buyer order-detail page).
//
// Availability of [Буцаах]: isOrderReturnable(order) && line.availableReturnQuantity > 0
// and the RETURN_CREATE grant. An unavailable button stays visible, aria-disabled,
// with its reason (B74): no grant → «Эрх хэрэгтэй: RETURN_CREATE»; not delivered
// yet (no window from the server) → «хүргэгдээгүй»; window over → «хугацаа дууссан» (B63).
import { ActionIcon, Avatar, Box, Button, Group, Stack, Text } from "@mantine/core";
import {
  ArrowUUpLeftIcon,
  CaretRightIcon,
  CheckCircleIcon,
  ClockIcon,
  ImageIcon,
  PackageIcon,
} from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { IReturnableOrderLine, IReturnOrderDetail } from "../../../apis/return";
import { currencyFormat } from "../../../utils";
import { ReasonTooltip } from "../../ui";
import { BUYER_RETURNS_ROUTES, isOrderReturnable, TNUM } from "./constants";
import classes from "./order-return-entry.module.css";

export interface OrderReturnEntryProps {
  order: IReturnOrderDetail;
  /** RETURN_CREATE grant — without it the buttons stay visible but aria-disabled with the reason. */
  canCreate: boolean;
}

// A line is "fully returned" when nothing remains AND something was returned.
function isFullyReturned(line: IReturnableOrderLine): boolean {
  return (line.availableReturnQuantity ?? 0) <= 0 && (line.alreadyReturnedQuantity ?? 0) > 0;
}

export function OrderReturnEntry({ order, canCreate }: OrderReturnEntryProps) {
  const { t } = useTranslation(["buyerApp", "common"]);
  const navigate = useNavigate();
  const lines = order?.lines ?? [];

  const eligible = isOrderReturnable(order);
  const daysRemaining = order?.returnWindowDaysRemaining;
  // B63: the server sends a window only once the order is delivered, so a null
  // expiry means «not delivered yet», not «expired».
  const windowReason = eligible
    ? null
    : order?.returnWindowExpiresAt
      ? t("buyerApp:entry.windowExpired")
      : t("buyerApp:entry.notDelivered");
  const blockedReason = !canCreate ? t("common:permission.required", { code: "RETURN_CREATE" }) : windowReason;

  const anyReturnable = lines.some((l) => (l.availableReturnQuantity ?? 0) > 0);

  const onReturnItem = (line: IReturnableOrderLine) => {
    navigate(`${BUYER_RETURNS_ROUTES.orderReturn(order.id)}?preselected=${line.id}`);
  };
  const onReturnAll = () => {
    navigate(`${BUYER_RETURNS_ROUTES.orderReturn(order.id)}?preselected=all`);
  };

  return (
    <Box className={classes.card}>
      <Group justify="space-between" align="center" mb="md" wrap="wrap" gap="sm">
        <Group gap="sm">
          <PackageIcon size={18} />
          <Text className={classes.title}>{t("buyerApp:entry.title")}</Text>
          <Box component="span" className={classes.count}>
            {lines.length}
          </Box>
        </Group>
        {eligible && daysRemaining != null ? (
          <Group gap={6} wrap="nowrap" align="center">
            <ClockIcon size={16} />
            <Text className={classes.meta} style={TNUM}>
              {t("buyerApp:entry.validRemaining", { days: daysRemaining })}
            </Text>
          </Group>
        ) : null}
      </Group>

      {windowReason ? <Text className={classes.notice}>{windowReason}</Text> : null}

      <Stack gap="sm">
        {lines.map((line) => {
          const fully = isFullyReturned(line);
          const partial = (line.alreadyReturnedQuantity ?? 0) > 0 && (line.availableReturnQuantity ?? 0) > 0;
          const hasQty = (line.availableReturnQuantity ?? 0) > 0;
          const returnNumbers = line.returnNumbers ?? [];
          const lineReason = blockedReason;

          return (
            <Box key={line.id} className={classes.line} data-faded={fully || undefined}>
              <Group wrap="nowrap" align="flex-start" justify="space-between">
                <Group wrap="nowrap" align="flex-start" gap="sm" miw={0} style={{ flex: 1 }}>
                  <Avatar src={line.image || null} size={44} radius="md" className={classes.thumb}>
                    <ImageIcon size={18} />
                  </Avatar>
                  <Box miw={0}>
                    <Text className={classes.name}>{line.name || "-"}</Text>
                    <Text className={classes.mono}>
                      SKU: {line.skuCode || "-"}
                      {line.unit ? ` · ${line.unit}` : ""}
                    </Text>
                    <Text className={classes.mono} style={TNUM}>
                      {currencyFormat(line.price || 0)} x {line.quantity ?? 0}
                    </Text>

                    {partial ? (
                      <Group gap={6} mt={6} wrap="nowrap" align="center">
                        <ArrowUUpLeftIcon size={16} />
                        <Text className={classes.meta}>
                          {t("buyerApp:entry.quantityReturned", { count: line.alreadyReturnedQuantity })}
                          {returnNumbers.length ? ` (${returnNumbers.join(", ")})` : ""}
                        </Text>
                      </Group>
                    ) : null}

                    {fully ? (
                      <Group gap={6} mt={6} wrap="nowrap" align="center">
                        <CheckCircleIcon size={16} weight="fill" color="var(--mantine-color-green-6)" />
                        <Text className={classes.meta}>
                          {t("buyerApp:entry.fullyReturned")}
                          {returnNumbers.length ? ` (${returnNumbers.join(", ")})` : ""}
                        </Text>
                      </Group>
                    ) : null}
                  </Box>
                </Group>

                <Box>
                  {hasQty ? (
                    <ReasonTooltip reason={lineReason || undefined}>
                      <Button variant="default" leftSection={<ArrowUUpLeftIcon size={16} />} onClick={() => onReturnItem(line)}>
                        {partial
                          ? t("buyerApp:entry.returnRemaining", { count: line.availableReturnQuantity })
                          : t("buyerApp:entry.return")}
                      </Button>
                    </ReasonTooltip>
                  ) : fully && returnNumbers.length ? (
                    <ActionIcon
                      variant="light"
                      color="gray"
                      size={32}
                      aria-label={t("buyerApp:entry.returnDetail")}
                      onClick={() => navigate(BUYER_RETURNS_ROUTES.hub)}
                    >
                      <CaretRightIcon size={16} />
                    </ActionIcon>
                  ) : null}
                </Box>
              </Group>
            </Box>
          );
        })}

        {lines.length === 0 ? (
          <Text ta="center" className={classes.meta} py="md">
            {t("buyerApp:entry.noItems")}
          </Text>
        ) : null}
      </Stack>

      {anyReturnable ? (
        <Group justify="flex-end" mt="md">
          <ReasonTooltip reason={blockedReason || undefined}>
            <Button color="brand" leftSection={<PackageIcon size={18} />} onClick={onReturnAll}>
              {t("buyerApp:entry.returnAll")}
            </Button>
          </ReasonTooltip>
        </Group>
      ) : null}
    </Box>
  );
}
